import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, filter, first } from 'rxjs/operators';

import { renderEnvironment } from 'environments/render/environment';
import { RequestParam } from 'idt-ecommerce-api/_api/api-util/api-request.models';
import { Category } from 'idt-ecommerce-api/catalog/categories/categories.models';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { UrlsWebHelpers } from 'idt-ecommerce-api/util/urls-web.helpers';

import { Link } from './links.model';


@Injectable()
export class LinksService {

  constructor(
    private readonly contextService: ContextService,
    private readonly productsService: ProductsService
  ) { }

  public getLinkUrl(link: Link, keepPlaceholders?: boolean): Observable<string | undefined> {
    const { type, categoryId, productId, slug, socialMediaLookId } = link;
    if ((type === 'category' || type === 'seoId') && categoryId) {
      return this.getCategoryUrl(link, categoryId, keepPlaceholders);
    }

    if (type === 'product' && productId) {
      return this.getProductUrl(link, productId, categoryId, keepPlaceholders);
    }

    if (type === 'page' && slug) {
      return this.contextService.getContext$().pipe(
        first(),
        map((context: EcommerceContext) => {
          return keepPlaceholders
            ? UrlsWebHelpers.getPageUrlWithPlaceholders(context, slug)
            : UrlsWebHelpers.getPageUrl(context, slug);
        })
      );
    }

    if (type === 'socialMediaLook') {
      return this.getSocialMediaLookUrl(socialMediaLookId);
    }

    if (type === 'customEvent' && link.eventCode) {
      return of(`javascript:${link.eventCode}`);
    }

    return of(link.src);
  }

  private getCategoryUrl(link: Link, categoryId: string, keepPlaceholders?: boolean): Observable<string | undefined> {
    return this.contextService.getContext$().pipe(
      filter((context) => isDefined(context.categories) && context.categories.length > 0),
      first(),
      map((context: EcommerceContext) => {
        if (keepPlaceholders) {
          return UrlsWebHelpers.getCategoryUrlWithPlaceholders(context, categoryId);
        }

        if (link.type === 'seoId') {
          const urlSeo = this.findUrlSeoBySeoId(context.categories, categoryId);
          if (urlSeo) {
            return urlSeo;
          }
        }

        if (link.enableUnifiedUrl && context.categories) {
          const urlSeo = this.findUrlSeoById(context.categories, categoryId);
          if (urlSeo) {
            return urlSeo;
          }
        }

        return UrlsWebHelpers.getCategoryUrl(context, categoryId);
      }),
      map((url) => {
        if (link.type === 'category' && link.initialBlockId) {
          return UrlsWebHelpers.addParamsToUrl(url, [{ key: 'initialBlockId', value: link.initialBlockId }]);
        }

        return url;
      })
    );
  }

  private getProductUrl(
    link: Link,
    productId: string,
    categoryId?: string,
    keepPlaceholders?: boolean
  ): Observable<string | undefined> {
    return combineLatest([
      this.productsService.storeInformationOfProducts$([productId]),
      this.contextService.getContext$()
    ]).pipe(
      first(),
      map(([products, context]: [Record<string, Product>, EcommerceContext]) => {
        const product = products[productId];
        let url: string;
        if (keepPlaceholders) {
          url = UrlsWebHelpers.getProductUrlWithPlaceholders(context, productId, categoryId);
        } else if (link.enableUnifiedUrl) {
          url = UrlsWebHelpers.getProductUrl(
            context,
            productId,
            categoryId,
            undefined,
            undefined,
            product.productUrl,
            product.productUrlParam
          );
        } else {
          url = UrlsWebHelpers.getProductUrl(context, productId, categoryId);
        }

        const params: RequestParam[] = [...link.extraQueryParams ?? []];
        if (link.productPartNumberDetails?.colorId) {
          params.push({
            key: renderEnvironment.ecommerce.colorIdParamName ?? 'colorId',
            value: link.productPartNumberDetails.colorId
          });
        }

        if (link.productPartNumberDetails?.style) {
          params.push({
            key: 'style',
            value: link.productPartNumberDetails.style
          });
        }

        link.src = !!params.length ? UrlsWebHelpers.addParamsToUrl(url, params) : url;

        return link.src;
      })
    );
  }

  private findUrlSeoById(categories: Category[], id: string, allCategories?: Category[]): string | undefined {
    allCategories = allCategories ?? categories;
    const matchedCategory = categories.find((category) => category.id === id);

    if (matchedCategory) {
      if (matchedCategory.redirectToCategoryId) {
        return this.findUrlSeoById(allCategories, matchedCategory.redirectToCategoryId, allCategories);
      }
      return matchedCategory.urlSeo;
    }

    for (const category of categories) {
      const urlSeo = this.findUrlSeoById(category.subcategories || [], id, allCategories);
      if (urlSeo) {
        return urlSeo;
      }
    }

    return undefined;
  }

  private findUrlSeoBySeoId(categories: Category[], id: string, allCategories?: Category[]): string | undefined {
    allCategories = allCategories ?? categories;
    const matchedCategory = categories.find((category) => category.urlSeo?.match(new RegExp(`[nl]${id}($|\\?)`)));

    if (matchedCategory) {
      if (matchedCategory.redirectToCategoryId) {
        return this.findUrlSeoById(allCategories, matchedCategory.redirectToCategoryId, allCategories);
      }
      return matchedCategory.urlSeo;
    }

    for (const category of categories) {
      const urlSeo = this.findUrlSeoBySeoId(category.subcategories || [], id, allCategories);
      if (urlSeo) {
        return urlSeo;
      }
    }

    return undefined;
  }

  private getSocialMediaLookUrl(socialMediaLookId: string | undefined): Observable<string | undefined> {
    return this.contextService.getContext$().pipe(
      map((context: EcommerceContext) => {
        socialMediaLookId = socialMediaLookId ?? '';

        const extension = '.html';
        let url = UrlsWebHelpers.getPageUrl(context, `pullandbear-community/${socialMediaLookId}`);

        if (url.endsWith(extension)) {
          url = url.replace(extension, '');
        }

        return url;
      })
    );
  }

}
